/**
 * WelcomeCreditsToast
 *
 * One-time celebratory toast (+ confetti) shown right after a brand-new user
 * signs up, telling them about their free credits. Triggered off the /sync
 * `isNewUser` flag. Designed NOT to collide with the cookie banner (waits until
 * consent is decided) or the take-a-tour prompt (suppresses it while shown), and
 * sits top-center, distinct from those.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserProfileContext } from '../../contexts/UserProfileContext';
import { hasConsent } from '../../services/consent/consentManager';
import { fireConfetti } from '../../utils/confetti';
import './WelcomeCreditsToast.css';

const SHOWN_KEY = '8bitcomposer_welcome_credits_shown';
const TOUR_OFFERED_KEY = '8bitcomposer_tutorial_offered';

export function WelcomeCreditsToast() {
  const { profile } = useUserProfileContext();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [credits, setCredits] = useState(0);
  const firedRef = useRef(false);
  // Re-evaluate once the cookie decision is made (so we don't stack on it).
  const [consentTick, setConsentTick] = useState(0);

  useEffect(() => {
    const onDismiss = () => setConsentTick((t) => t + 1);
    window.addEventListener('cookie-banner-dismissed', onDismiss);
    return () => window.removeEventListener('cookie-banner-dismissed', onDismiss);
  }, []);

  useEffect(() => {
    if (firedRef.current) return;
    if (!profile) return;
    // Only on the composer — sign-up first lands on /consent/email; the welcome
    // moment should happen once they reach the actual app.
    if (location.pathname !== '/' && location.pathname !== '/compose') return;
    // New sign-up signal: the /sync `isNewUser` flag, or an account created in the
    // last 10 min (createdAt is a core field, robust to the sync→session path).
    const createdRecently =
      !!profile.createdAt && Date.now() - Date.parse(profile.createdAt) < 10 * 60 * 1000;
    if (!profile.isNewUser && !createdRecently) return;
    if (!hasConsent()) return; // wait until the cookie banner is resolved
    try {
      if (localStorage.getItem(SHOWN_KEY)) return;
    } catch {
      /* ignore */
    }

    firedRef.current = true;
    setCredits(profile.currentCredits ?? 0);
    setShow(true);
    fireConfetti();
    try {
      localStorage.setItem(SHOWN_KEY, '1');
      // Suppress the take-a-tour offer so the two don't appear together.
      localStorage.setItem(TOUR_OFFERED_KEY, '1');
    } catch {
      /* ignore */
    }
    const t = setTimeout(() => setShow(false), 9000);
    return () => clearTimeout(t);
  }, [profile, consentTick, location.pathname]);

  if (!show) return null;

  return (
    <div className="welcome-credits-toast" role="status" aria-live="polite">
      <button
        className="welcome-credits-close"
        onClick={() => setShow(false)}
        aria-label="Dismiss welcome message"
      >
        ✕
      </button>
      <span className="welcome-credits-emoji" aria-hidden="true">🎉</span>
      <div className="welcome-credits-text">
        <strong>Welcome to 8-Bit Composer!</strong>
        <span>
          You've got <b>{credits} free credit{credits === 1 ? '' : 's'}</b> — enough to
          generate AI tracks and export. Have fun! 🎵
        </span>
      </div>
    </div>
  );
}
